import { useMutation } from '@apollo/client'

import { OrderStatus } from '__generated__/atlas-types'

import { atlasClient } from 'graphql/client'
import { atlas } from 'graphql/tags'
import { useRenderToasters } from 'hooks/useRenderToasters'

const BULK_UPDATE_ORDER_STATUS = atlas`#graphql
  mutation bulkUpdateOrderStatusMutation($input: BulkUpdateOrderStatusInput!) {
    bulkUpdateOrderStatus(input: $input) {
      updatedOrderIds
      errors {
        message
      }
    }
  }
`

type BulkUpdateOrderStatusInput = {
  orderIds: string[]
  status: OrderStatus
}

export const useBulkUpdateOrderStatus = () => {
  const { renderErrorToast, renderSuccessToast } = useRenderToasters()
  const [updateOrdersStatus, { loading }] = useMutation(
    BULK_UPDATE_ORDER_STATUS,
    {
      client: atlasClient,
      onError: () => {
        renderErrorToast({
          title: 'Error',
          description: 'There was an error updating the status. Try it later',
        })
      },
    },
  )

  const onBulkUpdateStatus = async (
    orderIds: string[],
    status: OrderStatus,
  ): Promise<string[] | undefined> => {
    const standardError = 'Error updating the orders.'
    try {
      if (!orderIds) {
        throw new Error(standardError)
      }

      const input: BulkUpdateOrderStatusInput = {
        orderIds,
        status,
      }

      const { data } = await updateOrdersStatus({
        variables: {
          input,
        },
      })

      if (
        data?.bulkUpdateOrderStatus?.errors.length ||
        data?.bulkUpdateOrderStatus?.updatedOrderIds?.length === 0
      ) {
        data.bulkUpdateOrderStatus.errors.map((error: { message: string }) =>
          renderErrorToast({ description: error.message, title: 'Error' }),
        )
        return
      }

      renderSuccessToast({
        description: `${data.bulkUpdateOrderStatus.updatedOrderIds.length} order(s) have been updated`,
      })

      return data.bulkUpdateOrderStatus.updatedOrderIds
    } catch (error) {
      throw new Error(standardError)
    }
  }

  return {
    isUpdating: loading,
    onBulkUpdateStatus,
  }
}
